import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

export interface HelpModalProps {
  open: boolean;
  onClose: () => void;
}

type Lang = 'fr' | 'en';

interface Row {
  label: ReactNode;
  key: string;
  value: string;
}

interface Section {
  title: string;
  rows: Row[];
}

const FR_SECTIONS: Section[] = [
  {
    title: 'Boutons de filtre',
    rows: [
      { key: 'map', label: '🗺', value: 'Basculer le style de carte (standard ↔ immersif)' },
      {
        key: 'subregions',
        label: (
          <svg viewBox="-12 -12 24 24" width="22" height="22" stroke="#0b0d12" strokeWidth="1.25" strokeLinejoin="round" aria-hidden="true">
            <circle cx="0" cy="0" r="10" fill="#3a3f4a" />
            <path d="M0 0 L0 -10 A10 10 0 0 1 8.66 5 Z" fill="#516C4B" />
            <path d="M0 0 L8.66 5 A10 10 0 0 1 -8.66 5 Z" fill="#245682" />
          </svg>
        ),
        value: 'Afficher / masquer les sous-régions de contrôle (Colonial, Warden, Neutre)',
      },
      { key: 'grid', label: '⊞', value: 'Afficher / masquer la grille' },
      {
        key: 'arrows',
        label: '👁 Flèches',
        value:
          'Panel gauche : cliquer sur "Flèches" déplie la liste pour cibler une flèche précise ; l\'icône 👁 masque/affiche toute la catégorie',
      },
    ],
  },
  {
    title: 'Raccourcis d\'édition',
    rows: [
      { key: 'delete', label: 'Suppr / Retour arrière', value: 'Supprimer l’icône ou la flèche sélectionnée (popover ouvert)' },
      { key: 'enter', label: 'Entrée', value: 'Valider le tracé d’une flèche' },
      { key: 'backspace', label: 'Retour arrière', value: 'Retirer le dernier point d’un tracé de flèche' },
      { key: 'esc', label: 'Échap', value: 'Annuler le tracé en cours / fermer popovers et modales' },
      { key: 'rightclick', label: 'Clic droit', value: 'Activer / désactiver un hexagone (mode Officier)' },
    ],
  },
  {
    title: 'Exports',
    rows: [
      { key: 'warplan', label: '📷 War plan', value: 'Capture image de la carte — icônes Combat & hubs logistiques (Hub, Seaport, Storage Depot, Airway) avec flèches' },
      { key: 'facbuild', label: '🏭 Fac-Build org', value: 'Export image dédié à l’organisation Facility / Build' },
      { key: 'msup', label: '🔧 MSup org', value: 'Export image dédié à l’organisation MSup / tunnels' },
      { key: 'operation', label: '📷 Opération', value: 'Capture image du mode Opération — toutes les icônes et flèches du plan tactique' },
    ],
  },
  {
    title: 'Rôles & permissions',
    rows: [
      { key: 'sandbox', label: 'Sandbox', value: 'Accessible à tous — bac à sable local, modifications non synchronisées' },
      { key: 'member', label: 'Membre', value: 'Lecture seule — consulte la carte, les assets et les exports' },
      {
        key: 'editor',
        label: 'Éditeur (Sergent / NCO)',
        value:
          'Peut tout faire SAUF placer/modifier des cibles (Combat) et les hubs logistiques principaux (Hub, Seaport, Storage Depot, Airway)',
      },
      { key: 'officer', label: 'Officier (Admin)', value: 'Accès complet — toutes les actions, gestion des hexagones et audit' },
    ],
  },
];

const EN_SECTIONS: Section[] = [
  {
    title: 'Filter buttons',
    rows: [
      { key: 'map', label: '🗺', value: 'Toggle map style (standard ↔ immersive)' },
      {
        key: 'subregions',
        label: (
          <svg viewBox="-12 -12 24 24" width="22" height="22" stroke="#0b0d12" strokeWidth="1.25" strokeLinejoin="round" aria-hidden="true">
            <circle cx="0" cy="0" r="10" fill="#3a3f4a" />
            <path d="M0 0 L0 -10 A10 10 0 0 1 8.66 5 Z" fill="#516C4B" />
            <path d="M0 0 L8.66 5 A10 10 0 0 1 -8.66 5 Z" fill="#245682" />
          </svg>
        ),
        value: 'Show / hide sub-regions control zones (Colonial, Warden, Neutral)',
      },
      { key: 'grid', label: '⊞', value: 'Show / hide the grid' },
      { key: 'arrows', label: '👁 Arrows', value: 'Show / hide arrows per category (left panel)' },
    ],
  },
  {
    title: 'Editing shortcuts',
    rows: [
      { key: 'delete', label: 'Delete / Backspace', value: 'Delete the selected icon or arrow (with popover open)' },
      { key: 'enter', label: 'Enter', value: 'Commit arrow drawing' },
      { key: 'backspace', label: 'Backspace', value: 'Remove last point of an arrow draft' },
      { key: 'esc', label: 'Esc', value: 'Cancel drawing / close popovers and modals' },
      { key: 'rightclick', label: 'Right-click', value: 'Enable / disable a hexagon (Officer mode)' },
    ],
  },
  {
    title: 'Exports',
    rows: [
      { key: 'warplan', label: '📷 War plan', value: 'Map snapshot — Combat icons & logistic hubs (Hub, Seaport, Storage Depot, Airway) with arrows' },
      { key: 'facbuild', label: '🏭 Fac-Build org', value: 'Dedicated image export for Facility / Build organization' },
      { key: 'msup', label: '🔧 MSup org', value: 'Dedicated image export for MSup / tunnels organization' },
      { key: 'operation', label: '📷 Operation', value: 'Operation mode snapshot — all icons and arrows from the tactical plan' },
    ],
  },
  {
    title: 'Roles & permissions',
    rows: [
      { key: 'sandbox', label: 'Sandbox', value: 'Available to everyone — local sandbox, changes are not synced' },
      { key: 'member', label: 'Member', value: 'Read-only — can view the map, assets and exports' },
      {
        key: 'editor',
        label: 'Editor (Sergeant / NCO)',
        value:
          'Can do everything EXCEPT placing/editing targets (Combat) and main logistic hubs (Hub, Seaport, Storage Depot, Airway)',
      },
      { key: 'officer', label: 'Officer (Admin)', value: 'Full access — all actions, hex management and audit log' },
    ],
  },
];

const STRINGS = {
  fr: {
    title: 'Aide',
    close: 'Fermer',
    contribute: 'Contribuer sur GitHub',
    contributeSuffix: '— issues, suggestions et pull requests bienvenues',
    sections: FR_SECTIONS,
  },
  en: {
    title: 'Help',
    close: 'Close',
    contribute: 'Contribute on GitHub',
    contributeSuffix: '— issues, suggestions and pull requests welcome',
    sections: EN_SECTIONS,
  },
} as const;

const GITHUB_URL = 'https://github.com/NapoSky/11ePlanner';

export function HelpModal({ open, onClose }: HelpModalProps): React.JSX.Element | null {
  const [lang, setLang] = useState<Lang>('fr');

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const t = STRINGS[lang];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-2xl max-h-[85vh] flex-col rounded border border-slate-800 bg-slate-900 text-slate-100 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-800 px-5 py-3">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold">{t.title}</h2>
            <div
              role="tablist"
              aria-label="Language"
              className="flex overflow-hidden rounded border border-slate-700 text-xs"
            >
              {(['fr', 'en'] as const).map((code) => (
                <button
                  key={code}
                  type="button"
                  role="tab"
                  aria-selected={lang === code}
                  onClick={() => setLang(code)}
                  className={
                    'px-2.5 py-1 font-semibold uppercase tracking-wide transition ' +
                    (lang === code
                      ? 'bg-slate-700 text-slate-100'
                      : 'bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-slate-200')
                  }
                >
                  {code}
                </button>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t.close}
            title={t.close}
            className="rounded p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
          >
            ✕
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {t.sections.map((section) => (
            <section key={section.title} className="mb-5 last:mb-0">
              <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                {section.title}
              </h3>
              <table className="w-full text-sm">
                <tbody>
                  {section.rows.map((row, i) => (
                    <tr
                      key={row.key}
                      className={
                        i < section.rows.length - 1 ? 'border-b border-slate-800' : ''
                      }
                    >
                      <td className="w-1/3 py-1.5 pr-4 align-top font-medium text-slate-200">
                        {row.label}
                      </td>
                      <td className="py-1.5 align-top text-slate-400">{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </div>

        <footer className="shrink-0 border-t border-slate-800 px-5 py-3 text-center text-xs text-slate-400">
          <a
            href={GITHUB_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 font-semibold text-sky-400 hover:text-sky-300 hover:underline"
          >
            <span aria-hidden="true">★</span>
            {t.contribute}
          </a>{' '}
          <span>{t.contributeSuffix}</span>
        </footer>
      </div>
    </div>
  );
}
